import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputBase from "@mui/material/InputBase";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import { MaterialInputProps, StandardInputProps } from "./AppTextField.props";
import React, { ChangeEvent, ForwardedRef, forwardRef, useState } from "react";
import {
  VerifiedRounded as VerifiedOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { UseInputStyles } from "./AppTextField.styles";

const DEFAULT_LABEL_FONT = "MBCorpoAScreen1Cond-Regular-Web";

export const PasswordInputField = forwardRef(
  function PasswordInputFieldComponent(
    {
      label,
      className,
      required,
      onChange = () => {},
      value,
      readOnly = false,
      fontFamilyForInput = "",
    }: MaterialInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ): JSX.Element {
    const InputStyle = UseInputStyles();
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const effectiveFontFamily =
      fontFamilyForInput || "MBCorpoAScreen1Cond-Regular-Web";

    function handleClickShowPassword(): void {
      setShowPassword((prev) => !prev);
    }

    const handleMouseDownPassword = (event) => {
      event.preventDefault();
    };

    return (
      <div className={"w-full h-full " + className}>
        <FormControl fullWidth className={InputStyle.password}>
          <InputLabel
            sx={{
              fontFamily: DEFAULT_LABEL_FONT,
            }}
          >
            {label}
          </InputLabel>
          <OutlinedInput
            ref={ref}
            disabled={readOnly}
            required={required}
            type={showPassword ? "text" : "password"}
            value={value || ""}
            onChange={(e) => {
              onChange(
                e.currentTarget.value,
                e as ChangeEvent<HTMLInputElement>
              );
            }}
            sx={{
              fontFamily: effectiveFontFamily,
            }}
            inputProps={{
              style: {
                fontFamily: effectiveFontFamily,
              },
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </div>
    );
  }
);

export const MaterialInput = forwardRef(function MaterialInputComponent(
  {
    className = "",
    onChange = () => {},
    readOnly = false,
    variant = "standard",
    type = "text",
    value = "",
    InputColor = false,
    fontFamilyForInput = "",
    ...props
  }: MaterialInputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const effectiveFontFamily =
    fontFamilyForInput || "MBCorpoAScreen1Cond-Regular-Web";

  if (type === "password") {
    return (
      <PasswordInputField
        {...props}
        ref={ref}
        type={type}
        variant={variant}
        disabled={readOnly}
        onChange={(value, e) => {
          onChange((e.currentTarget as HTMLInputElement).value, e);
        }}
        fontFamilyForInput={fontFamilyForInput}
        fullWidth
      />
    );
  }

  return (
    <div className={`${className}`}>
      <div style={{ marginBottom: "7px" }}>
        {props.showlabel && (
          <label
            className="whitespace-nowrap"
            style={{
              color: "#FFFFFF",
              fontFamily: DEFAULT_LABEL_FONT,
            }}
          >
            {props.label}
          </label>
        )}
      </div>
      <TextField
        autoComplete={Date.now().toString()}
        ref={ref}
        {...props}
        label={props.label}
        value={value || ""}
        type={type}
        style={{
          backgroundColor: InputColor ? "#D9D9D9" : "#FFFFFF",
          width: props.containerwidth,
          height: props.containerheight,
          borderRadius: "8px",
          fontFamily: effectiveFontFamily,
        }}
        InputProps={{
          sx: {
            height: props.containerheight,
            fontFamily: effectiveFontFamily,
            color: "#1E1E1E",
            display: "flex",
            alignItems: "center",
            padding: 0,
          },
          inputProps: {
            style: {
              padding: "0 14px",
              marginLeft: "10px",
              fontFamily: effectiveFontFamily,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            fontFamily: effectiveFontFamily,
            "&.MuiInputLabel-root": {
              left: "10px",
            },
            "&.MuiInputLabel-shrink": {
              display: "none",
            },
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
        variant="outlined"
        disabled={readOnly}
        onChange={(e) => {
          onChange(e.currentTarget.value, e as ChangeEvent<HTMLInputElement>);
        }}
      />
    </div>
  );
});

export const StandardInput = forwardRef(function StandardInputComponent(
  {
    label,
    type,
    className,
    required,
    isVerified,
    onChange = () => {},
    placeholder,
    value,
    accept,
    readOnly = false,
    fontFamilyForInput = "",
    ...props
  }: StandardInputProps,
  ref: ForwardedRef<HTMLInputElement>
): JSX.Element {
  const InputStyle = UseInputStyles();
  const effectiveFontFamily =
    fontFamilyForInput || "MBCorpoAScreen1Cond-Regular-Web";

  return (
    <div className={"w-full h-full " + className}>
      <FormControl fullWidth className={InputStyle.standard}>
        {label && (
          <InputLabel
            shrink
            sx={{
              fontFamily: DEFAULT_LABEL_FONT,
            }}
          >
            {label}
            {required && <span className="text-red-600"> *</span>}
            {isVerified && (
              <VerifiedOutlined
                titleAccess="verified"
                sx={{ fontSize: "0.9rem" }}
                className="text-green-800"
              />
            )}
          </InputLabel>
        )}
        <InputBase
          ref={ref}
          disabled={readOnly}
          value={value || ""}
          onChange={(e) => {
            onChange(e.currentTarget.value, e);
          }}
          required={required}
          type={type}
          placeholder={placeholder}
          componentsProps={{ input: { accept } }}
          fullWidth
          sx={{
            fontFamily: effectiveFontFamily,
          }}
          inputProps={{
            style: {
              fontFamily: effectiveFontFamily,
            },
          }}
          {...props}
        />
      </FormControl>
    </div>
  );
});
